import { useGetDriverScheduleQuery, useGetMultipleDriverSchedulesQuery, useCreateDriverScheduleMutation, useUpdateDriverScheduleMutation, useDeleteDriverScheduleMutation } from '../gql/driverSchedule.generated';
import { DAY_NAMES } from '@features/settings/utils/businessHoursUtils';
import { useMemo } from 'react';

export const useDriverSchedule = (driverId: string | undefined) => {
  const days = [0, 1, 2, 3, 4, 5, 6]; // Sunday-Saturday

  // Query for fetching driver schedule
  const { data, loading, error, refetch } = useGetDriverScheduleQuery({
    variables: { driverId: driverId as any },
    skip: !driverId,
  });

  // Mutations for CRUD operations
  const [createDriverScheduleMutation, { loading: creating }] = useCreateDriverScheduleMutation({
    refetchQueries: ['GetDriverSchedule'],
  });

  const [updateDriverScheduleMutation, { loading: updating }] = useUpdateDriverScheduleMutation({
    refetchQueries: ['GetDriverSchedule'],
  });

  const [deleteDriverScheduleMutation, { loading: deleting }] = useDeleteDriverScheduleMutation({
    refetchQueries: ['GetDriverSchedule'],
  });

  const driverSchedule = data?.businesshours || [];

  return {
    driverSchedule,
    loading,
    error,
    creating,
    updating,
    deleting,
    refetch,
    createDriverSchedule: createDriverScheduleMutation,
    updateDriverSchedule: updateDriverScheduleMutation,
    deleteDriverSchedule: deleteDriverScheduleMutation,
    days,
    dayNames: DAY_NAMES,
  };
};

/**
 * Hook to fetch schedules for multiple drivers at once.
 * Groups schedules by driver ID for easy access.
 */
export const useMultipleDriverSchedules = (driverIds: number[]) => {
  const { data, loading, error } = useGetMultipleDriverSchedulesQuery({
    variables: { driverIds },
    skip: driverIds.length === 0,
  });

  // Group schedules by driver ID
  const schedulesByDriverId = useMemo(() => {
    const result = new Map<number, NonNullable<typeof data>['businesshours']>();
    if (!data?.businesshours) return result;

    // Get default hours (driver_id is null)
    const defaultHours = data.businesshours.filter((h) => h.driver_id === null);

    // Group driver-specific schedules
    driverIds.forEach((driverId) => {
      const driverSpecific = data.businesshours.filter((h) => h.driver_id === driverId);
      // Combine driver-specific schedules with default hours
      result.set(driverId, [...driverSpecific, ...defaultHours]);
    });

    return result;
  }, [data?.businesshours, driverIds]);

  return {
    schedulesByDriverId,
    loading,
    error,
  };
};
